import React, { useEffect, useRef, useState } from "react";
import { SidebarProvider, useSidebar, SIDEBAR_WIDTH_EXPANDED_PX, SIDEBAR_WIDTH_COLLAPSED_PX } from "../context/SidebarContext";
import { Outlet, useLocation } from "react-router-dom";
import AppHeader from "./AppHeader";
import Backdrop from "./Backdrop";
import AppSidebar from "./AppSidebar";

const LayoutGridInner: React.FC = () => {
  const { isExpanded, closeMobileSidebar } = useSidebar();
  const [headerTitle, setHeaderTitle] = useState<string | undefined>();
  const [isDesktop, setIsDesktop] = useState<boolean>(
    typeof window !== "undefined" ? window.innerWidth >= 1024 : false,
  );
  const [leftOffset, setLeftOffset] = useState<number>(isExpanded ? SIDEBAR_WIDTH_EXPANDED_PX : SIDEBAR_WIDTH_COLLAPSED_PX);
  const location = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const offset = isExpanded ? SIDEBAR_WIDTH_EXPANDED_PX : SIDEBAR_WIDTH_COLLAPSED_PX;
    setLeftOffset(offset);
  }, [isExpanded]);

  // Close mobile sidebar on page navigation, but keep desktop sidebar open
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    closeMobileSidebar();
  }, [location.pathname]);

  const desktopPaddingLeft = isDesktop ? leftOffset : 0;

  return (
    <div className="min-h-screen bg-white text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      {/* Single AppSidebar instance handles both desktop and mobile layouts internally */}
      <AppSidebar />

      <Backdrop />

      <div
        className="min-h-screen flex flex-col transition-all duration-300"
        style={{ paddingLeft: desktopPaddingLeft }}
      >
        <header className="w-full transition-all duration-300">
          <AppHeader title={headerTitle} />
        </header>

        <main className="flex-1 w-full">
          <div className="w-full max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8 pt-[28px] sm:pt-[72px] md:pt-20">
            <Outlet context={{ setHeaderTitle }} />
          </div>
        </main>
      </div>
    </div>
  );
};

const AppLayout: React.FC = () => (
  <SidebarProvider>
    <LayoutGridInner />
  </SidebarProvider>
);

export default AppLayout;
